/**
 * Centralized metric definitions for Growth Analytics.
 *
 * Each entry defines:
 *   key               – unique identifier (used to look up the metric)
 *   label             – display name
 *   shortDefinition   – one-line description shown in tooltips
 *   formula           – human-readable formula (shown in tooltips and drawer)
 *   sqlQuery          – underlying DB query for technical reference (drawer only)
 *   whyItMatters      – business relevance
 *   caveat            – known limitation or data-quality note
 *   dataSource        – underlying model / field
 *   recommendedUsage  – suggested context of use (e.g. "investor update", "ops review")
 */

const METRICS = {

    // ── TRACTION ──────────────────────────────────────────────────────────────

    totalTipVolume: {
        key: 'totalTipVolume',
        label: 'Volume Mance (GTV)',
        shortDefinition: 'Somma di tutti i pagamenti mance con status "succeeded" nel periodo.',
        formula: 'Σ importo di tutte le mance riuscite nel periodo',
        sqlQuery: 'SUM(TipPaymentIntent.amount) WHERE status = "succeeded" AND transactionDate IN period',
        whyItMatters: 'Misura la traction reale della piattaforma: quanto denaro fluisce attraverso Soontip.',
        caveat: 'Questo è il volume lordo (GTV), NON la revenue di Soontip. La revenue è rappresentata dalla fee trattenuta.',
        dataSource: 'TipPaymentIntent.amount',
        recommendedUsage: 'Investor update, traction analysis, pricing decisions',
    },

    successfulTransactions: {
        key: 'successfulTransactions',
        label: 'Transazioni Andate a Buon Fine',
        shortDefinition: 'Numero di pagamenti mance completati con successo.',
        formula: '# transazioni con esito "riuscito" nel periodo',
        sqlQuery: 'COUNT(TipPaymentIntent) WHERE status = "succeeded" AND transactionDate IN period',
        whyItMatters: 'Indicatore di utilizzo reale del prodotto.',
        caveat: 'Non include tentativi falliti o pagamenti in stato pending.',
        dataSource: 'TipPaymentIntent.status',
        recommendedUsage: 'Traction analysis, engagement tracking',
    },

    averageTipAmount: {
        key: 'averageTipAmount',
        label: 'Mancia Media',
        shortDefinition: 'Valore medio per singola transazione di mancia andata a buon fine.',
        formula: 'Volume GTV ÷ Numero transazioni riuscite',
        sqlQuery: 'SUM(amount) / COUNT(*) WHERE status = "succeeded" AND transactionDate IN period',
        whyItMatters: 'Indica la generosità media dei clienti e il valore unitario del prodotto.',
        caveat: 'Sensibile a outlier: una singola mancia molto alta può distorcere la media su periodi brevi.',
        dataSource: 'Calcolato da TipPaymentIntent',
        recommendedUsage: 'Benchmarking venue, analisi stagionalità',
    },

    paymentSuccessRate: {
        key: 'paymentSuccessRate',
        label: 'Payment Success Rate',
        shortDefinition: 'Percentuale di tentativi di pagamento andati a buon fine.',
        formula: 'Transazioni riuscite ÷ (Riuscite + Fallite) × 100',
        sqlQuery: 'COUNT(succeeded) / COUNT(succeeded + failed) * 100 FROM TipPaymentIntent WHERE period',
        whyItMatters: 'Un tasso alto indica buona UX e qualità del flusso di pagamento.',
        caveat: 'Include solo tentativi con esito determinabile: SUCCESS (status succeeded o terminalOutcome finale positivo) e FAILURE (status failed/payment_failed, terminalOutcome finale negativo o last_payment_error persistito). requires_payment_method senza evidenza di failure resta fuori denominatore.',
        dataSource: 'TipPaymentIntent.status, TipPaymentIntent.terminalOutcome, TipPaymentIntent.metadata.last_payment_error',
        recommendedUsage: 'Monitoraggio operativo, SLA pagamenti',
    },

    activeVenues: {
        key: 'activeVenues',
        label: 'Locali Attivi',
        shortDefinition: 'Locali che hanno registrato almeno una transazione riuscita nel periodo.',
        formula: '# locali con almeno 1 transazione riuscita nel periodo',
        sqlQuery: 'COUNT(DISTINCT venueId) FROM TipPaymentIntent WHERE status = "succeeded" AND transactionDate IN period',
        whyItMatters: "Misura l'adozione reale del prodotto da parte dei clienti (tenant).",
        caveat: 'Un locale con device attivo ma zero transazioni nel periodo risulta inattivo. Estendere il periodo per una visione più accurata.',
        dataSource: 'TipPaymentIntent.venueId',
        recommendedUsage: 'Investor update, customer success, upsell',
    },

    venueActivationRate: {
        key: 'venueActivationRate',
        label: 'Venue Activation Rate',
        shortDefinition: 'Percentuale di locali totali che hanno avuto almeno una transazione nel periodo.',
        formula: 'Locali attivi ÷ Totale locali × 100',
        sqlQuery: 'COUNT(DISTINCT venueId FROM tips) / COUNT(Venue) * 100',
        whyItMatters: 'Indica quanta parte del portafoglio clienti è operativa.',
        caveat: 'Dipende dal periodo selezionato: periodi brevi producono tassi più bassi.',
        dataSource: 'TipPaymentIntent + Venue',
        recommendedUsage: 'Customer health, churn risk assessment',
    },

    // ── REVENUE ───────────────────────────────────────────────────────────────

    estimatedSoontipFee: {
        key: 'estimatedSoontipFee',
        label: 'Fee Stimata Soontip',
        shortDefinition: 'Stima della revenue Soontip calcolata dagli snapshot di fee al momento del pagamento.',
        formula: 'Σ [ importo × (% fee / 100) + fee fissa ] per ogni transazione riuscita',
        sqlQuery: 'SUM((amount * soontipFeePercentApplied / 100) + (soontipFeeFixedApplied / 100)) WHERE status = "succeeded"',
        whyItMatters: 'Approssimazione della revenue Soontip quando le fatture non sono ancora emesse.',
        caveat: 'VALORE STIMATO, non fiscalmente certificativo. Valido solo per le transazioni con snapshot fee disponibile. Copertura parziale se soontipFeePercentApplied o soontipFeeFixedApplied sono NULL.',
        dataSource: 'TipPaymentIntent.soontipFeePercentApplied, TipPaymentIntent.soontipFeeFixedApplied',
        recommendedUsage: 'Investor update (con disclaimer), internal tracking',
    },

    invoicedSoontipFee: {
        key: 'invoicedSoontipFee',
        label: 'Fatture emesse nel periodo',
        shortDefinition: 'Importo lordo delle fatture Soontip con stato ACCEPTED, DELIVERED o SENT, filtrate per data emissione nel periodo.',
        formula: 'Σ amountGross delle fatture emesse (Accepted / Sent / Delivered) nel periodo',
        sqlQuery: 'SUM(Invoice.amountGross) WHERE status IN (ACCEPTED, DELIVERED, SENT) AND invoiceDate IN period',
        whyItMatters: 'Unico valore fiscalmente rilevante per la revenue Soontip. Più conservativo della stima fee.',
        caveat: 'ATTENZIONE: filtrato per data emissione fattura, NON per data transazione. Può includere fatture relative a periodi precedenti. NON usare come "Revenue confermata" senza riconciliazione esplicita con le transazioni del periodo.',
        dataSource: 'Invoice.amountGross, Invoice.status, Invoice.invoiceDate',
        recommendedUsage: 'Contabilità, riconciliazione amministrativa. NON per investor reporting senza nota di perimetro.',
    },

    pendingRevenue: {
        key: 'pendingRevenue',
        label: 'Revenue Pending (Non Fatturata)',
        shortDefinition: 'Fee stimata su transazioni del periodo per cui non è ancora stata emessa una fattura.',
        formula: 'Fee stimata − Revenue già fatturata',
        sqlQuery: 'estimatedSoontipFee - SUM(Invoice.amountGross WHERE status IN confirmed)',
        whyItMatters: 'Indica quanto "revenue pipeline" non è ancora contabilizzata.',
        caveat: 'Approssimazione: il calcolo non è una riconciliazione 1:1 tra transazioni e fatture.',
        dataSource: 'Calcolato da TipPaymentIntent + Invoice',
        recommendedUsage: 'Cash flow planning, billing operations',
    },

    // ── ACTIVATION ────────────────────────────────────────────────────────────

    workerKycCompletionRate: {
        key: 'workerKycCompletionRate',
        label: 'Stripe Connect Completion Rate',
        shortDefinition: "Percentuale di percettori che hanno completato l'onboarding Stripe Connect (stato can_receive_payouts).",
        formula: 'Percettori con Stripe completato ÷ Percettori che hanno avviato l\'onboarding × 100',
        sqlQuery: 'COUNT(WorkerPaymentProfile WHERE stripeConnectStatus = "can_receive_payouts") / COUNT(WorkerPaymentProfile WHERE stripeConnectStatus != "not_started") * 100',
        whyItMatters: 'I percettori che non completano non possono ricevere i payout. Indica friction nel flusso KYC.',
        caveat: 'Dato all-time, non filtrabile per periodo. Riflette lo stato attuale dei WorkerPaymentProfile.',
        dataSource: 'WorkerPaymentProfile.stripeConnectStatus',
        recommendedUsage: 'Worker onboarding ops, Stripe compliance',
    },

    activeWorkers: {
        key: 'activeWorkers',
        label: 'Percettori con Invito Accettato',
        shortDefinition: 'Numero di percettori che hanno accettato un invito (WorkerInvite status = ACCEPTED).',
        formula: '# percettori con invito accettato',
        sqlQuery: 'COUNT(WorkerInvite) WHERE status = "ACCEPTED"',
        whyItMatters: 'Indica il bacino di percettori attivi nel sistema.',
        caveat: 'Non implica che il percettore abbia completato Stripe o ricevuto payout.',
        dataSource: 'WorkerInvite.status',
        recommendedUsage: 'Network size, payout eligibility analysis',
    },

    // ── OPERATIONS ────────────────────────────────────────────────────────────

    activeDevices: {
        key: 'activeDevices',
        label: 'Device Attivi',
        shortDefinition: 'Numero di totem/device con status ACTIVE.',
        formula: '# device con stato "ACTIVE"',
        sqlQuery: 'COUNT(Device) WHERE status = "ACTIVE"',
        whyItMatters: 'Solo i device attivi possono accettare pagamenti. Un device non attivo causa interruzione del servizio per il locale.',
        caveat: 'Status aggiornato in real-time, non dipende dal filtro periodo.',
        dataSource: 'Device.status',
        recommendedUsage: 'Operational monitoring, SLA device',
    },

    offlineDevices: {
        key: 'offlineDevices',
        label: 'Device Offline',
        shortDefinition: 'Device non in stato ACTIVE (es. SHIPPED_PENDING_ACTIVATION, DISABLED, MAINTENANCE).',
        formula: '# device con stato diverso da "ACTIVE"',
        sqlQuery: 'COUNT(Device) WHERE status != "ACTIVE"',
        whyItMatters: 'Ogni device offline rappresenta un locale impossibilitato a raccogliere mance.',
        caveat: 'Include device in fase di setup (SHIPPED_PENDING_ACTIVATION), che non sono tecnicamente "in avaria".',
        dataSource: 'Device.status',
        recommendedUsage: 'Prioritizzazione customer support, field ops',
    },

    failedPayments: {
        key: 'failedPayments',
        label: 'Pagamenti Falliti',
        shortDefinition: 'Numero di tentativi di pagamento per cui esiste evidenza certa di esito negativo nel periodo.',
        formula: 'COUNT dove (status=failed/payment_failed) OPPURE (terminalOutcome finale negativo) OPPURE (requires_payment_method + last_payment_error persistito)',
        sqlQuery: 'COUNT(*) FILTER (WHERE status IN ("failed","payment_failed") OR terminalOutcome IN ("CONFIRM_FAILED","COLLECT_DECLINED","COLLECT_FAILED") OR (status="requires_payment_method" AND metadata last_payment_error IS NOT NULL)) FROM TipPaymentIntent WHERE transactionDate IN period',
        whyItMatters: "Impatta direttamente l'esperienza utente e la fiducia nel prodotto.",
        caveat: 'requires_payment_method senza last_payment_error persistito è classificato OTHER (non FAILURE): potrebbe essere un PaymentIntent appena creato o non ancora completato. Non include pending/processing/canceled.',
        dataSource: 'TipPaymentIntent.status, TipPaymentIntent.terminalOutcome, TipPaymentIntent.metadata.last_payment_error',
        recommendedUsage: 'Ops monitoring, Stripe dispute analysis',
    },

    pendingPayments: {
        key: 'pendingPayments',
        label: 'Pagamenti Pending',
        shortDefinition: 'Transazioni in stato "pending" o "requires_action" nel periodo.',
        formula: '# transazioni non ancora risolte (né riuscite né fallite)',
        sqlQuery: 'COUNT(TipPaymentIntent) WHERE status NOT IN ("succeeded", "failed") AND transactionDate IN period',
        whyItMatters: 'Indica transazioni non risolte che potrebbero diventare fallite o richiedere intervento.',
        caveat: 'Lo stato è dinamico: un pagamento pending può risolversi entro poche ore.',
        dataSource: 'TipPaymentIntent.status',
        recommendedUsage: 'Monitoraggio operativo real-time',
    },

    invoiceRunFailures: {
        key: 'invoiceRunFailures',
        label: 'Invoice Run con Errori',
        shortDefinition: 'Numero di cicli di fatturazione automatica (InvoiceRun) con status FAILED o PARTIAL nel periodo.',
        formula: '# cicli di fatturazione con esito FAILED o PARTIAL',
        sqlQuery: 'COUNT(InvoiceRun) WHERE status IN ("FAILED", "PARTIAL") AND startedAt IN period',
        whyItMatters: 'Un invoice run fallito significa che alcuni percettori non hanno ricevuto la fattura e potenzialmente il payout.',
        caveat: 'Un invoice run può essere PARTIAL (alcune fatture ok, alcune fallite). Verificare invoicesFailed per il dettaglio.',
        dataSource: 'InvoiceRun.status, InvoiceRun.invoicesFailed',
        recommendedUsage: 'Billing ops, percettori a rischio payout',
    },

    acubeFailures: {
        key: 'acubeFailures',
        label: 'Errori Integrazione Fatturazione (Acube)',
        shortDefinition: 'Fatture con errore di trasmissione verso il sistema di fatturazione elettronica (Acube/SDI).',
        formula: '# fatture non trasmesse correttamente al Sistema di Interscambio (SDI)',
        sqlQuery: 'COUNT(Invoice) WHERE acubeStatus = "ERROR" AND invoiceDate IN period',
        whyItMatters: 'Indica fatture non trasmesse al sistema fiscale italiano. Richiede intervento manuale.',
        caveat: 'Dato non ancora esposto nella dashboard. Richiede implementazione futura.',
        dataSource: 'Invoice.acubeStatus (non ancora implementato)',
        recommendedUsage: 'Compliance fiscale, fatturazione elettronica',
    },

    // ── DISTRIBUZIONE MANCE ───────────────────────────────────────────────────

    tipDistributedTotal: {
        key: 'tipDistributedTotal',
        label: 'Totale distribuito',
        shortDefinition: 'Somma delle quote di mancia assegnate ai percettori tramite TipTransfer nel periodo.',
        formula: 'Σ TipTransfer.amount per il periodo e scope selezionati',
        sqlQuery: 'SUM(TipTransfer.amount) GROUP BY period',
        whyItMatters: 'Misura quanto denaro è stato concretamente assegnato ai lavoratori tramite la piattaforma.',
        caveat: 'Non corrisponde necessariamente al payout bancario liquidato. Un TipTransfer assegnato può essere in pending o avere errori di payout.',
        dataSource: 'TipTransfer.amount',
        recommendedUsage: 'Ops review, verifica distribuzione',
    },

    tipAvgPerWorker: {
        key: 'tipAvgPerWorker',
        label: 'Media per percettore',
        shortDefinition: 'Totale distribuito diviso per il numero di percettori distinti nel periodo.',
        formula: 'Totale distribuito ÷ N percettori distinti',
        sqlQuery: 'SUM(TipTransfer.amount) / COUNT(DISTINCT TipTransfer.destination)',
        whyItMatters: 'Indica quanto ogni percettore riceve in media. Utile per valutare l\'impatto economico per lavoratore.',
        caveat: 'La media può essere distorta se un percettore ha ricevuto mance in molti più periodi rispetto agli altri.',
        dataSource: 'TipTransfer.amount, TipTransfer.destination',
        recommendedUsage: 'Analisi impatto economico lavoratori',
    },

    tipSpreadDistribution: {
        key: 'tipSpreadDistribution',
        label: 'Spread distribuzione',
        shortDefinition: 'Differenza tra il percettore con importo massimo e quello con importo minimo nel periodo.',
        formula: 'MAX(totale per percettore) − MIN(totale per percettore)',
        sqlQuery: 'MAX(worker_total) - MIN(worker_total) FROM aggregated TipTransfer',
        whyItMatters: 'Indica quanto è equa la distribuzione tra i percettori. Spread elevato = distribuzione molto sbilanciata.',
        caveat: 'Uno spread di €0 può indicare distribuzione perfettamente uniforme oppure un solo percettore.',
        dataSource: 'TipTransfer.amount aggregato per destination',
        recommendedUsage: 'Equità distribuzione, analisi operativa',
    },

    tipTopWorker: {
        key: 'tipTopWorker',
        label: 'Top percettore',
        shortDefinition: 'Percettore (o percettori in caso di parità) con il totale assegnato più alto nel periodo.',
        formula: 'MAX(totale per percettore)',
        sqlQuery: 'ORDER BY SUM(TipTransfer.amount) DESC LIMIT 1',
        whyItMatters: 'Identifica chi ha beneficiato di più dalla distribuzione mance nel periodo.',
        caveat: 'In caso di parità vengono mostrati tutti i percettori con lo stesso importo massimo.',
        dataSource: 'TipTransfer.amount aggregato per destination',
        recommendedUsage: 'Analisi singoli percettori',
    },

    tipVenueVolume: {
        key: 'tipVenueVolume',
        label: 'Volume mance locale',
        shortDefinition: 'Somma delle transazioni mancia riuscite (TipPaymentIntent) per il locale nel periodo.',
        formula: 'Σ TipPaymentIntent.amount WHERE status = "succeeded"',
        sqlQuery: 'SUM(TipPaymentIntent.amount) WHERE status = "succeeded" AND venueId = X AND period',
        whyItMatters: 'Rappresenta il totale lordo incassato dal locale. Serve come riferimento per la riconciliazione con il totale distribuito.',
        caveat: 'Questo è il volume lato pagamento (TipPaymentIntent). Possono esistere differenze rispetto al totale distribuito per via di fee, arrotondamenti o TipTransfer in stato pending.',
        dataSource: 'TipPaymentIntent.amount',
        recommendedUsage: 'Riconciliazione, ops review',
    },

    tipReconciliationDiff: {
        key: 'tipReconciliationDiff',
        label: 'Differenza riconciliazione',
        shortDefinition: 'Differenza tra il volume mance del locale e il totale distribuito via TipTransfer.',
        formula: 'Volume mance locale − Totale distribuito',
        sqlQuery: 'SUM(TipPaymentIntent.amount) - SUM(TipTransfer.amount) per periodo e scope',
        whyItMatters: 'Idealmente prossima a zero. Una differenza significativa indica fee applicate, TipTransfer mancanti o transfer in stato pending.',
        caveat: 'Differenze piccole (< €0.01) sono normali per arrotondamenti. Differenze grandi richiedono verifica manuale.',
        dataSource: 'TipPaymentIntent.amount vs TipTransfer.amount',
        recommendedUsage: 'Riconciliazione contabile, compliance',
    },
};

/**
 * Get a metric definition by key.
 * Returns undefined if the key is not found.
 */
export function getMetric(key) {
    return METRICS[key];
}

/**
 * Get multiple metric definitions by key array.
 * Skips keys not found.
 */
export function getMetrics(keys) {
    return keys.map(k => METRICS[k]).filter(Boolean);
}

export default METRICS;
